/**
 * MYD Feature Collection — mobile scroll-hint + back-to-first control.
 */
(function () {
  const MOBILE_MQ = '(max-width: 989px)';
  const NUDGE_PX = 36;
  const OUT_MS = 260;
  const HOLD_MS = 180;
  const BACK_MS = 240;
  const START_DELAY_MS = 450;
  const LAYOUT_RETRY_MS = 200;
  const LAYOUT_RETRY_MAX = 10;

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function overflowAmount(scroller) {
    return scroller.scrollWidth - scroller.clientWidth;
  }

  function canHint(scroller) {
    if (!scroller || scroller.dataset.mydYmlHintDone === '1') return false;
    if (!isMobile() || prefersReducedMotion()) return false;
    return overflowAmount(scroller) > 24 && scroller.scrollLeft < 8;
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animateScrollLeft(scroller, from, to, duration) {
    return new Promise((resolve) => {
      if (duration <= 0 || Math.abs(to - from) < 1) {
        scroller.scrollLeft = to;
        resolve();
        return;
      }
      const start = performance.now();
      const step = (now) => {
        if (scroller.dataset.mydYmlHintCancelled === '1') {
          resolve();
          return;
        }
        const t = Math.min(1, (now - start) / duration);
        scroller.scrollLeft = from + (to - from) * easeOutCubic(t);
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          scroller.scrollLeft = to;
          resolve();
        }
      };
      requestAnimationFrame(step);
    });
  }

  function nudgeDistance(scroller) {
    const card = scroller.querySelector('.myd-yml-carousel__card');
    const cardW = card?.offsetWidth || Math.round(scroller.clientWidth * 0.45);
    const ideal = Math.round(cardW * 0.12);
    const max = Math.max(0, overflowAmount(scroller) - 4);
    return Math.min(NUDGE_PX, Math.max(24, ideal), max);
  }

  function runHint(scroller) {
    if (scroller.dataset.mydYmlHintRunning === '1') return;

    let tries = 0;
    const tryStart = () => {
      if (scroller.dataset.mydYmlHintDone === '1') return;
      if (!isMobile() || prefersReducedMotion()) {
        scroller.dataset.mydYmlHintDone = '1';
        return;
      }
      if (!canHint(scroller)) {
        tries += 1;
        if (tries < LAYOUT_RETRY_MAX) {
          window.setTimeout(tryStart, LAYOUT_RETRY_MS);
        } else {
          scroller.dataset.mydYmlHintDone = '1';
        }
        return;
      }
      play(scroller);
    };

    window.setTimeout(tryStart, START_DELAY_MS);
  }

  async function play(scroller) {
    if (scroller.dataset.mydYmlHintDone === '1' || scroller.dataset.mydYmlHintRunning === '1') return;
    scroller.dataset.mydYmlHintRunning = '1';
    delete scroller.dataset.mydYmlHintCancelled;

    const prevSnap = scroller.style.scrollSnapType;
    const prevBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    scroller.classList.add('is-scroll-hinting');

    const finish = () => {
      scroller.dataset.mydYmlHintCancelled = '1';
      scroller.classList.remove('is-scroll-hinting');
      scroller.style.scrollSnapType = prevSnap;
      scroller.style.scrollBehavior = prevBehavior;
      scroller.scrollLeft = 0;
      scroller.dataset.mydYmlHintDone = '1';
      delete scroller.dataset.mydYmlHintRunning;
      scroller.removeEventListener('pointerdown', onUser, true);
      scroller.removeEventListener('touchstart', onUser, true);
      scroller.removeEventListener('wheel', onUser, true);
    };

    const onUser = () => finish();
    scroller.addEventListener('pointerdown', onUser, { capture: true, passive: true });
    scroller.addEventListener('touchstart', onUser, { capture: true, passive: true });
    scroller.addEventListener('wheel', onUser, { capture: true, passive: true });

    const distance = nudgeDistance(scroller);
    if (distance < 16) {
      finish();
      return;
    }

    try {
      await animateScrollLeft(scroller, 0, distance, OUT_MS);
      if (scroller.dataset.mydYmlHintCancelled === '1') return;
      await new Promise((r) => window.setTimeout(r, HOLD_MS));
      if (scroller.dataset.mydYmlHintCancelled === '1') return;
      await animateScrollLeft(scroller, scroller.scrollLeft, 0, BACK_MS);
    } finally {
      if (scroller.dataset.mydYmlHintCancelled !== '1') {
        scroller.classList.remove('is-scroll-hinting');
        scroller.style.scrollSnapType = prevSnap;
        scroller.style.scrollBehavior = prevBehavior;
        scroller.scrollLeft = 0;
        scroller.dataset.mydYmlHintDone = '1';
        delete scroller.dataset.mydYmlHintRunning;
        scroller.removeEventListener('pointerdown', onUser, true);
        scroller.removeEventListener('touchstart', onUser, true);
        scroller.removeEventListener('wheel', onUser, true);
      }
    }
  }

  function scrollHintEnabled(scroller) {
    if (!scroller?.hasAttribute('data-myd-yml-scroll-hint')) return false;
    // Presence alone used to enable the hint; "false" must stay off.
    const raw = scroller.getAttribute('data-myd-yml-scroll-hint');
    if (raw == null || raw === '') return true;
    const v = String(raw).trim().toLowerCase();
    return v !== 'false' && v !== '0' && v !== 'off' && v !== 'no';
  }

  function bindScrollHint(scroller) {
    if (!scroller || scroller.dataset.mydYmlHintBound === '1') return;
    if (!scrollHintEnabled(scroller)) return;
    scroller.dataset.mydYmlHintBound = '1';

    if (!('IntersectionObserver' in window)) {
      runHint(scroller);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          runHint(scroller);
        });
      },
      { threshold: [0.12, 0.25], rootMargin: '0px' }
    );
    io.observe(scroller);
  }

  function cardSelector() {
    return '.myd-yml-carousel__card, .myd-cart__yml-card, [class*="ai-you-might-like-product-"]';
  }

  function syncBackSlot(scroller) {
    const slot = scroller.querySelector('[data-myd-yml-back-slot]');
    if (!slot) return;
    // Need room to scroll past products — hide if strip doesn't overflow meaningfully
    const cards = scroller.querySelectorAll(cardSelector()).length;
    const max = overflowAmount(scroller);
    const hide = cards < 2 || max < 40;
    slot.classList.toggle('is-hidden', hide);
  }

  function isBackSlotInView(scroller, slot) {
    if (!slot || slot.classList.contains('is-hidden')) return false;
    const sRect = scroller.getBoundingClientRect();
    const bRect = slot.getBoundingClientRect();
    // Slot is meaningfully visible inside the scroller viewport
    const visibleLeft = Math.max(sRect.left, bRect.left);
    const visibleRight = Math.min(sRect.right, bRect.right);
    const visible = Math.max(0, visibleRight - visibleLeft);
    return visible >= Math.min(48, bRect.width * 0.45);
  }

  function scrollToStart(scroller) {
    scroller.scrollTo({
      left: 0,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  function bindBackButton(scroller) {
    if (!scroller || scroller.dataset.mydYmlBackBound === '1') return;
    const btn = scroller.querySelector('[data-myd-yml-back]');
    const slot = scroller.querySelector('[data-myd-yml-back-slot]');
    if (!btn || !slot) return;
    scroller.dataset.mydYmlBackBound = '1';

    let autoTimer = null;
    let armed = false;

    const clearAuto = () => {
      if (autoTimer) {
        window.clearTimeout(autoTimer);
        autoTimer = null;
      }
      armed = false;
    };

    const armAutoReturn = () => {
      if (prefersReducedMotion()) return;
      if (armed || autoTimer) return;
      if (!isBackSlotInView(scroller, slot)) return;
      armed = true;
      autoTimer = window.setTimeout(() => {
        autoTimer = null;
        armed = false;
        if (!isBackSlotInView(scroller, slot)) return;
        scrollToStart(scroller);
      }, 5000);
    };

    const onScroll = () => {
      syncBackSlot(scroller);
      if (isBackSlotInView(scroller, slot)) {
        armAutoReturn();
      } else {
        clearAuto();
      }
    };

    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      clearAuto();
      scrollToStart(scroller);
    });

    // User interaction while waiting cancels the countdown
    scroller.addEventListener(
      'pointerdown',
      () => {
        if (autoTimer) clearAuto();
      },
      { passive: true }
    );

    syncBackSlot(scroller);
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (document.fonts?.ready) {
      document.fonts.ready.then(onScroll).catch(() => {});
    }
    window.setTimeout(onScroll, 300);
    window.setTimeout(onScroll, 1000);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && isBackSlotInView(scroller, slot)) {
              armAutoReturn();
            } else if (!entry.isIntersecting) {
              clearAuto();
            }
          });
        },
        { root: scroller, threshold: [0.35, 0.55] }
      );
      io.observe(slot);
    }
  }

  // Scroll-position dots — same active-tracking approach as the quick-add
  // sheet's image gallery dots (myd-search.js syncDots).
  function bindDots(scroller) {
    if (!scroller || scroller.dataset.mydYmlDotsBound === '1') return;
    const dotsEl = scroller.parentElement
      ? scroller.parentElement.querySelector('[data-myd-yml-dots]')
      : null;
    if (!dotsEl) return;
    scroller.dataset.mydYmlDotsBound = '1';

    const syncDots = () => {
      const cards = scroller.querySelectorAll(cardSelector());
      if (!cards.length) return;
      const gap = parseFloat(getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap || '0') || 0;
      const step = cards[0].offsetWidth + gap;
      if (!step) return;
      const index = Math.min(cards.length - 1, Math.max(0, Math.round(scroller.scrollLeft / step)));
      dotsEl.querySelectorAll('span').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    };

    scroller.addEventListener('scroll', syncDots, { passive: true });
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-myd-yml-scroller]').forEach((scroller) => {
      bindBackButton(scroller);
      bindScrollHint(scroller);
      bindDots(scroller);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target));
  document.addEventListener('myd:yml-init', (event) => init(event.target || document));

  window.MYDYmlCarousel = { init };
})();
